import React, { useState, useRef, useEffect } from "react";
import { useSelectFunctions, useUpdateFunctions } from "../../functions";
import { successMsg } from "../SuccessHandler";
import Input from "../../utilities/Input";
const EmailConfigurationModal = ({ show, onClose, data }) => {
  const closeButtonRef = useRef();
  const [showPassword, setShowPassword] = useState(false);
  const [emailConfigurationData, setEmailConfigurationData] = useState({
    FromEmail: "",
    RecipientsEmail: [],
    Server: "",
    Port: 0,
    Encryption: "NONE",
    Account: "",
    Password: "",
    Enable: false,
  });
  const { getEmailConfig } = useSelectFunctions();
  const { updateEmailConfig } = useUpdateFunctions();
  const [emailError, setEmailError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [recipientInput, setRecipientInput] = useState("");
  const [formErrors, setFormErrors] = useState({});

  // SHOW/HIDE PASSWORD
  const toggleShowPassword = () => {
    setShowPassword((prevShow) => !prevShow);
  };

  // GET EMAIL CONFIGURATION INFO
  useEffect(() => {
    if (!show || !data?.UUID) return;

    const fetchData = async () => {
      const res = await getEmailConfig(data.UUID);

      if (res) {
        setEmailConfigurationData({
          FromEmail: res.FromEmail || "",
          RecipientsEmail: res.RecipientsEmail || [],
          Server: res.Server || "",
          Port: res.Port || 0,
          Encryption: res.Encryption || "",
          Account: res.Account || "",
          Password: res.Password || "",
          Enable: res.Enable ?? false,
        });
      }
    };

    fetchData();
  }, [show]);

  // ONCHANGE INFO
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormErrors((prev) => ({
      ...prev,
      [name]: null,
    }));

    if (name === "RecipientsEmail") {
      setEmailConfigurationData((prev) => ({
        ...prev,
        RecipientsEmail: value.split(",").map((email) => email.trim()),
      }));
      return;
    }

    if (name === "Port") {
      setEmailConfigurationData((prev) => ({
        ...prev,
        Port: Number(value),
      }));
      return;
    }

    setEmailConfigurationData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // VALIDATE FORM INFO
  const validateForm = () => {
    const data = emailConfigurationData;
    const errors = {};

    if (!data.FromEmail || !isValidEmail(data.FromEmail)) {
      errors.FromEmail = true;
    }

    if (!data.RecipientsEmail || data.RecipientsEmail.length === 0) {
      errors.RecipientsEmail = true;
    }

    if (!data.Server) {
      errors.Server = true;
    }

    if (!data.Port || isNaN(data.Port)) {
      errors.Port = true;
    }

    if (!data.Account) {
      errors.Account = true;
    }

    if (!data.Password) {
      errors.Password = true;
    }

    return errors;
  };

  // UPDATE INFO
  const handleSave = async () => {
    try {
      const errors = validateForm();

      if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        return;
      }

      const LogonSessionID = data.UUID;

      const request = {
        LogonSessionID,
        EmailConfig: {
          ...emailConfigurationData,
          RecipientsEmail: emailConfigurationData.RecipientsEmail.filter(
            (email) => email,
          ),
        },
      };

      const res = await updateEmailConfig(request, LogonSessionID);

      if (res?.OutParams?.Result === 0) {
        successMsg("Email Configuration Info has been changed successfully");
        onClose();
      } else {
        setFormErrors({
          general: res?.OutParams?.ErrMsg || "Error saving data",
        });
      }
    } catch (error) {
      setFormErrors({ general: "Unexpected error" });
    }
  };

  // VALID EMAIL
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateRecipients = (list) => {
    if (!list || list.length === 0) {
      setFormErrors((prev) => ({
        ...prev,
        RecipientsEmail: true,
      }));
      return false;
    }

    setFormErrors((prev) => ({
      ...prev,
      RecipientsEmail: null,
    }));

    return true;
  };

  // ADD RECIPIENTS EMAIL
  const addRecipient = (email) => {
    const cleanEmail = email.trim();

    if (!cleanEmail) return;

    if (!isValidEmail(cleanEmail)) {
      setEmailError(true);
      setErrorMessage("Invalid email");
      return;
    }

    if (emailConfigurationData.RecipientsEmail.includes(cleanEmail)) return;

    const updated = [...emailConfigurationData.RecipientsEmail, cleanEmail];

    setEmailConfigurationData((prev) => ({
      ...prev,
      RecipientsEmail: updated,
    }));

    validateRecipients(updated);

    setRecipientInput("");
    setEmailError(false);
    setErrorMessage("");
  };

  // KEY DOWN RECIPIENTS EMAILS
  const handleKeyDown = (e) => {
    const value = recipientInput.trim();

    if (e.key === "Enter" || e.key === "," || e.key === " ") {
      e.preventDefault();
      addRecipient(value);
      return;
    }

    if (e.key === "Tab") {
      if (!value) return;

      if (isValidEmail(value)) {
        e.preventDefault();
        addRecipient(value);

        setTimeout(() => {
          e.target.blur();
        }, 0);
      } else {
        setRecipientInput("");
        setEmailError(false);
      }

      return;
    }

    if (e.key === "Backspace" && !recipientInput) {
      setEmailConfigurationData((prev) => ({
        ...prev,
        RecipientsEmail: prev.RecipientsEmail.slice(0, -1),
      }));
    }
  };

  // DELETE RECIPIENTS EMAILS
  const removeRecipient = (index) => {
    const updated = emailConfigurationData.RecipientsEmail.filter(
      (_, i) => i !== index,
    );

    setEmailConfigurationData((prev) => ({
      ...prev,
      RecipientsEmail: updated,
    }));

    validateRecipients(updated);
  };

  // OnBlur RECIPIENTS EMAILS
  const handleBlur = () => {
    const value = recipientInput.trim();

    if (value) {
      if (!isValidEmail(value)) {
        setRecipientInput("");
        setEmailError(false);
      } else {
        addRecipient(value);
      }
    }

    validateRecipients([...emailConfigurationData.RecipientsEmail]);
  };

  // OnPaste RECIPIENTS EMAILS
  const handlePaste = (e) => {
    e.preventDefault();

    const paste = e.clipboardData.getData("text");
    const emails = paste.split(/[,;\s]+/);

    const validEmails = [];

    emails.forEach((email) => {
      const clean = email.trim();
      if (isValidEmail(clean)) {
        validEmails.push(clean);
      }
    });

    if (validEmails.length) {
      setEmailConfigurationData((prev) => {
        const updated = [...prev.RecipientsEmail, ...validEmails];

        validateRecipients(updated);

        return {
          ...prev,
          RecipientsEmail: updated,
        };
      });

      setRecipientInput("");
      setEmailError(false);
    } else {
      setRecipientInput(paste);
      setEmailError(true);
      setErrorMessage("Invalid email");
    }
  };

  const isDisabled = !emailConfigurationData.Enable;

  return (
    <>
      {show && <div className="modal-backdrop fade show"></div>}
      <div
        className={`modal fade ${show ? "show d-block" : ""}`}
        aria-labelledby="emailConfigurationModal"
        aria-hidden={!show}
      >
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header dialog-modal">
              <h5
                className="modal-title"
                id="emailConfigurationModal"
                style={{ color: "#99ddff" }}
              >
                Email Configuration
              </h5>
            </div>

            <div
              className="modal-body table-clients pt-0"
              style={{ color: "#4d4d4d" }}
            >
              <div className="col-sm-12">
                <div className="custom-control custom-switch mb-1 mt-1 d-flex justify-content-end">
                  <input
                    type="checkbox"
                    className="custom-control-input m-1"
                    id="switchDate"
                    name="Enable"
                    checked={emailConfigurationData.Enable}
                    onChange={(e) => {
                      setEmailConfigurationData((prev) => ({
                        ...prev,
                        Enable: e.target.checked,
                      }));
                    }}
                  />
                  <label
                    className="custom-control-label lead"
                    htmlFor="switchDate"
                    style={{ fontSize: "14px" }}
                  >
                    Enabled Information
                  </label>
                </div>
                <div
                  style={{
                    opacity: isDisabled ? 0.55 : 1,
                    pointerEvents: isDisabled ? "none" : "auto",
                    filter: isDisabled ? "grayscale(1)" : "none",
                    transition: "0.2s ease",
                  }}
                >
                  <div className="scheduler-div-border">
                    <span className="scheduler-border lead">
                      Email Information
                    </span>
                    <hr className="my-1" style={{ color: "#17a2b8" }} />
                    <div className="form-inline">
                      <Input
                        label="From"
                        name="FromEmail"
                        value={emailConfigurationData.FromEmail}
                        onChange={handleChange}
                        error={formErrors.FromEmail}
                      />
                    </div>
                    <div className="form-inline">
                      <label className="lead mr-2" style={{ fontSize: "16px" }}>
                        Email Recipients
                      </label>

                      <div
                        className={`form-control d-flex flex-wrap align-items-center p-1 ${
                          emailError || formErrors.RecipientsEmail
                            ? "is-invalid border-danger"
                            : ""
                        }`}
                        style={{
                          position: "relative",
                          maxHeight: "150px",
                          overflow: "auto",
                        }}
                      >
                        {emailConfigurationData.RecipientsEmail.map(
                          (email, index) => (
                            <div
                              key={index}
                              style={{
                                padding: "5px 5px",
                                margin: "2px",
                                background: "#e6e6e6",
                                borderRadius: "8px",
                                display: "flex",
                                alignItems: "center",
                                fontSize: "12px",
                                color: "#000",
                              }}
                            >
                              <span>{email}</span>
                              <span
                                style={{ marginLeft: "8px", cursor: "pointer" }}
                                onClick={() => removeRecipient(index)}
                              >
                                ×
                              </span>
                            </div>
                          ),
                        )}

                        <div
                          style={{
                            position: "relative",
                            flex: 1,
                            minWidth: "120px",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: "50%",
                              left: 0,
                              transform: "translateY(-50%)",
                              pointerEvents: "none",
                              color: emailError ? "red" : "transparent",
                              whiteSpace: "pre",
                              fontSize: "14px",
                            }}
                          >
                            {recipientInput}
                          </div>

                          <input
                            type="text"
                            value={recipientInput}
                            onChange={(e) => {
                              const value = e.target.value;
                              setRecipientInput(value);

                              if (emailError) setEmailError(false);

                              if (
                                value ||
                                emailConfigurationData.RecipientsEmail.length >
                                  0
                              ) {
                                setFormErrors((prev) => ({
                                  ...prev,
                                  RecipientsEmail: null,
                                }));
                              }
                            }}
                            onKeyDown={handleKeyDown}
                            onBlur={handleBlur}
                            onPaste={handlePaste}
                            style={{
                              background: "transparent",
                              color: emailError ? "transparent" : "#000",
                              caretColor: "#000",
                              border: "none",
                              outline: "none",
                              width: "100%",
                              fontSize: "14px",
                            }}
                            placeholder={
                              emailConfigurationData.RecipientsEmail.length ===
                              0
                                ? "Type email and press Enter"
                                : ""
                            }
                          />

                          {emailError && recipientInput && (
                            <div
                              title="Invalid email format"
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: `${recipientInput.length * 7.5}px`,
                                transform: "translateY(-50%)",
                                width: "6px",
                                height: "6px",
                                backgroundColor: "red",
                                borderRadius: "50%",
                                cursor: "pointer",
                              }}
                            />
                          )}
                        </div>
                      </div>
                      {formErrors.RecipientsEmail && (
                        <div className="invalid-feedback d-block">Required</div>
                      )}
                    </div>
                  </div>
                  <div className="scheduler-div-border">
                    <span className="scheduler-border lead">
                      SMTP Information
                    </span>
                    <hr className="my-1" style={{ color: "#17a2b8" }} />
                    <div className="row">
                      <div className="col-xl-6">
                        <div className="form-inline">
                          <Input
                            label="Server"
                            name="Server"
                            value={emailConfigurationData.Server}
                            onChange={handleChange}
                            error={formErrors.Server}
                          />
                        </div>
                      </div>
                      <div className="col-xl-6">
                        <div className="form-inline">
                          <Input
                            label="Port"
                            name="Port"
                            type="number"
                            value={emailConfigurationData.Port}
                            onChange={handleChange}
                            error={formErrors.Port}
                          />
                        </div>
                      </div>
                      <div className="form-inline">
                        <Input
                          label="Encryption"
                          name="Encryption"
                          type="select"
                          value={emailConfigurationData.Encryption || "NONE"}
                          onChange={handleChange}
                          options={[
                            { value: "NONE", label: "NONE" },
                            { value: "SSL", label: "SSL" },
                            { value: "TLS", label: "TLS" },
                            { value: "AUTO", label: "AUTO" },
                          ]}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="scheduler-div-border">
                    <span className="scheduler-border lead">
                      SMTP Authentication
                    </span>
                    <hr className="my-1" style={{ color: "#17a2b8" }} />
                    <div className="form-inline mt-2">
                      <Input
                        label="Account"
                        name="Account"
                        value={emailConfigurationData.Account}
                        onChange={handleChange}
                        error={formErrors.Account}
                        maxLength={255}
                      />
                    </div>
                    <div className="form-inline">
                      <label className="lead mr-2" style={{ fontSize: "16px" }}>
                        Password
                      </label>
                      <form autoComplete="off">
                        <div className="input-group">
                          <input
                            name="Password"
                            type={showPassword ? "text" : "password"}
                            className={`form-control ${
                              formErrors.Password ? "is-invalid" : ""
                            }`}
                            value={emailConfigurationData.Password}
                            onChange={handleChange}
                            maxLength={255}
                          />

                          <div
                            className="input-group-text"
                            onClick={toggleShowPassword}
                          >
                            <i
                              className={`fas ${showPassword ? "fa-eye" : "fa-eye-slash"}`}
                            />
                          </div>
                        </div>
                        {formErrors.Password && (
                          <div className="invalid-feedback d-block">
                            Required
                          </div>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-success"
                onClick={() => handleSave()}
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailConfigurationModal;
